import React, { ComponentType, HTMLAttributes, ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';

import CheckCircleIcon from '@videoblocks/react-icons/CheckCircle';
import CloseIcon from '@videoblocks/react-icons/Close';
import InformationCircleIcon from '@videoblocks/react-icons/InformationCircle';
import WarningCircleIcon from '@videoblocks/react-icons/WarningCircle';
import WarningTriangleIcon from '@videoblocks/react-icons/WarningTriangle';

const SEVERITIES = ['error', 'info', 'neutral', 'success', 'warning'] as const;
export type AlertSeverity = (typeof SEVERITIES)[number];

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The severity of the alert. This determines the alert color and icon.
   */
  severity?: AlertSeverity;
  /**
   * If provided, will cause the close icon to show up and will be
   * called on click of the close button.
   */
  onClose?(): void;
  /**
   * Whether the icon mapped to severity should be displayed.
   * Note that `neutral` does not have an icon.
   */
  icon?: boolean;
}

interface SeverityDetail {
  Icon: ComponentType<{ className: string }>;
  iconClassName: string;
  outerClassName: string;
}

const severityDetails: Record<AlertSeverity, SeverityDetail> = {
  error: {
    Icon: WarningCircleIcon,
    iconClassName: 'text-red-500',
    outerClassName: 'text-red-700 bg-red-100 border-red-500',
  },
  info: {
    Icon: InformationCircleIcon,
    iconClassName: 'text-blue-500',
    outerClassName: 'text-blue-700 bg-blue-100 border-blue-500',
  },
  neutral: {
    Icon: null,
    iconClassName: null,
    outerClassName: 'text-white bg-gray-800 border-gray-500',
  },
  success: {
    Icon: CheckCircleIcon,
    iconClassName: 'text-green-500',
    outerClassName: 'text-green-700 bg-green-100 border-green-500',
  },
  warning: {
    Icon: WarningTriangleIcon,
    iconClassName: 'text-orange-500',
    outerClassName: 'text-orange-700 bg-orange-100 border-orange-500',
  },
};

/**
 * An informational component used for conveying important messages.
 * @param severity
 * @param icon
 * @param onClose
 * @param className
 * @param children
 * @param rest
 * @constructor
 */
export default function Alert({
  severity,
  icon = true,
  onClose,
  className,
  children,
  ...rest
}: AlertProps): ReactElement {
  if (!SEVERITIES.includes(severity)) {
    // Sanity check that we only render severities we know about.
    severity = 'success';
  }
  const severityDetail = severityDetails[severity];

  const Icon = icon && severityDetail.Icon;

  return (
    <div
      className={twMerge(
        'py-6 px-4 rounded border-0 border-solid border-l-8 flex',
        severityDetail.outerClassName,
        className
      )}
      {...rest}
    >
      {Icon && (
        <div className={twMerge('flex mr-4', severityDetail.iconClassName)}>
          <Icon className="inline-block w-6 h-6 fill-current" />
        </div>
      )}
      <div>{children}</div>
      {onClose && (
        <div className="flex items-center pl-4 ml-auto">
          <button
            onClick={onClose}
            className="focus:outline-none focus-visible:ring p-1.5 rounded-full cursor-pointer border-0"
          >
            <CloseIcon className="w-3 h-3 fill-current" />
          </button>
        </div>
      )}
    </div>
  );
}
